import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  MobileStepper,
  Paper,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { useTutorialSteps } from '../../pages/help/TutorialSteps';

interface HeaderProps {
  toggleSidebar: () => void;
}

const Header: React.FC<HeaderProps> = ({ toggleSidebar }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const steps = useTutorialSteps(user ?? undefined);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = React.useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const [infoOpen, setInfoOpen] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const handleInfoOpen = () => setInfoOpen(true);
  const handleInfoClose = () => setInfoOpen(false);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={toggleSidebar}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          {t('app.title')}
        </Typography>

        {user && (
          <>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
              <Button
                color="inherit"
                component={RouterLink}
                to="/map"
                sx={{ ml: 1 }}
              >
                {t('navigation.sensorsMap')}
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/sensors"
                sx={{ ml: 1 }}
              >
                {t('navigation.sensors')}
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/alerts"
                sx={{ ml: 1 }}
              >
                {t('navigation.alerts')}
              </Button>
            </Box>

          <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
            <Tooltip title={t('common.man')}>
              <IconButton color="inherit" onClick={handleInfoOpen}>
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ ml: 2 }}>
            <Tooltip title={t('common.notifications')}>
              <IconButton
                size="large"
                aria-label="show new notifications"
                aria-controls="notifications-menu"
                aria-haspopup="true"
                onClick={handleNotificationsMenu}
                color="inherit"
              >
                <Badge badgeContent={0} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Menu
              id="notifications-menu"
              anchorEl={notificationsAnchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(notificationsAnchorEl)}
              onClose={handleNotificationsClose}
            >
              <MenuItem onClick={handleNotificationsClose}>
                {t('common.noNotifications')}
              </MenuItem>
            </Menu>
          </Box>

            <Box sx={{ ml: 2 }}>
              <Tooltip title={t('common.accountSettings')}>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={() => navigate('/profile')}
                  color="inherit"
                >
                  {user.full_name ? (
                    <Avatar alt={user.full_name} src="/static/avatar.jpg" />
                  ) : (
                    <AccountCircle />
                  )}
                </IconButton>
              </Tooltip>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem disabled>
                  {user.username} {user.is_admin && `(${t('common.admin')})`}
                </MenuItem>
                <MenuItem onClick={handleLogout}>{t('auth.logout')}</MenuItem>
              </Menu>
            </Box>
          </>
        )}
      </Toolbar>

      <Dialog open={infoOpen} onClose={handleInfoClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          {steps[activeStep].label}
        </DialogTitle>
        <DialogContent>
          <Paper elevation={0} sx={{ p: 2, minHeight: 80 }}>
            {steps[activeStep].description}
          </Paper>
        </DialogContent>
        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          nextButton={
            <Button
              size="small"
              onClick={handleNext}
              disabled={activeStep === steps.length - 1}
            >
              {t('tutorial.next')}
            </Button>
          }
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              {t('tutorial.previous')}
            </Button>
          }
        />
      </Dialog>
    </AppBar>
  );
};

export default Header;